import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type UIEventHandler,
  type WheelEventHandler,
} from 'react';
import type { TableProps } from '@/shared/types/table';
import { Table } from '@/modules/Table';
import {
  GhanttConfigProvider,
  GhanttScrollVirtualRoot,
  createGhanttScrollVirtualStore,
  ghanttModifierZoomNormalizeInitial,
  ghanttAnchorMsToContentPixel,
  ghanttBuildTimelineSlots,
  ghanttBuildUpperFloorSegments,
  ghanttContentPixelToAnchorMs,
  ghanttDefaultColumnWidthForScale,
  ghanttModifierZoomWheelDelta,
  ghanttParseTime,
  ghanttResolveExpandedDayRange,
  ghanttScrollVirtualIndexRange,
  ghanttSnapSlotWidthPx,
  GHANTT_SCROLL_VIRTUAL_MIN_SLOTS,
  type GhanttColumnDef,
  type GhanttConfigContextValue,
  type GhanttTimelineProps,
  type GhanttTimelineScale,
} from '@/shared/ghantt';
import { mergeGhanttColumns } from './mergeGhanttColumns';

function omitModifierWheelZoom(t: GhanttTimelineProps): GhanttTimelineProps {
  const { modifierWheelZoom: _ignored, ...rest } = t;
  return rest;
}

/**
 * X del puntero respecto al borde izquierdo del **mismo** elemento cuyo `scrollLeft` define el desplazamiento
 * (viewport de cabecera). Si se mide con el padre del track del cuerpo, thead/tbody pueden desalinearse
 * (p. ej. scrollbar vertical solo en el cuerpo) y `scrollLeft + x` cae en el inicio del contenido → anclas en ~2015.
 */
function ghanttWheelTimelineViewportClientX(
  scrollViewportEl: HTMLElement | null,
  clientX: number
): number {
  const el = scrollViewportEl;
  if (!el) return 0;
  const r = el.getBoundingClientRect();
  const w = el.clientWidth > 0 ? el.clientWidth : r.width;
  return Math.max(0, Math.min(w, clientX - r.left));
}

const DEFAULT_APPEARANCE = {
  trackMinHeightPx: 44,
} as const;

const DEFAULT_EVENTS = {} as const;

/**
 * Tabla estándar {@link Table} con **una columna extra** de tipo Gantt (`type: 'gantt'`).
 * La configuración voluminosa vive en el objeto **`gantt`** (presentación, datos, timeline, aspecto, eventos).
 *
 * @example
 * ```tsx
 * <Ghantt
 *   columns={nameCols}
 *   rows={rows}
 *   gantt={{
 *     column: {
 *       header: <GhanttTimelineHeader title="Cronograma" />,
 *       width: 520,
 *       placement: 'end',
 *     },
 *     data: { accessor: 'timeline' },
 *     timeline: { viewStart: '2026-04-01', viewEnd: '2026-05-01', scale: 'day' },
 *     events: { onBarClick: ({ bar }) => console.log(bar.id) },
 *   }}
 * />
 * ```
 */
export function Ghantt<T>({
  columns,
  gantt,
  ...tableProps
}: Omit<TableProps<T>, 'columns'> & {
  columns: TableProps<T>['columns'];
  gantt: GhanttColumnDef<T>;
}) {
  const modifierWheelZoom = gantt.timeline.modifierWheelZoom === true;

  const initialManagedTimeline: GhanttTimelineProps = modifierWheelZoom
    ? ghanttModifierZoomNormalizeInitial(gantt.timeline)
    : gantt.timeline;

  const [managedTimeline, setManagedTimeline] =
    useState<GhanttTimelineProps>(initialManagedTimeline);

  const effectiveTimeline = modifierWheelZoom ? managedTimeline : gantt.timeline;

  /** Timeline ya aplicada en pantalla (evita calcular el ancla con un `prev` distinto del scroll tras varios zooms seguidos). */
  const managedTimelineRef = useRef(effectiveTimeline);
  managedTimelineRef.current = effectiveTimeline;

  const timelineForContext = useMemo(
    () => omitModifierWheelZoom(effectiveTimeline),
    [effectiveTimeline]
  );

  const timelineScrollKey = useMemo(
    () =>
      `${effectiveTimeline.viewStart}|${effectiveTimeline.viewEnd}|${effectiveTimeline.scale ?? 'day'}|${
        effectiveTimeline.includeLeadingFullMonth !== false
      }|${effectiveTimeline.columnWidthPx ?? ''}|${effectiveTimeline.columnMaxWidthPx ?? ''}`,
    [
      effectiveTimeline.viewStart,
      effectiveTimeline.viewEnd,
      effectiveTimeline.scale,
      effectiveTimeline.includeLeadingFullMonth,
      effectiveTimeline.columnWidthPx,
      effectiveTimeline.columnMaxWidthPx,
    ]
  );

  const mergedColumns = useMemo(() => mergeGhanttColumns(columns, gantt), [columns, gantt]);

  const scrollVirtualStore = useMemo(() => createGhanttScrollVirtualStore(), []);

  const appearance = useMemo(
    () => ({ ...DEFAULT_APPEARANCE, ...gantt.appearance }),
    [gantt.appearance]
  );

  const events = useMemo(() => ({ ...DEFAULT_EVENTS, ...gantt.events }), [gantt.events]);

  const onTimelineChangeRef = useRef(gantt.events?.onTimelineChange);
  onTimelineChangeRef.current = gantt.events?.onTimelineChange;

  useEffect(() => {
    if (!modifierWheelZoom) return;
    onTimelineChangeRef.current?.(omitModifierWheelZoom(managedTimeline));
  }, [modifierWheelZoom, managedTimeline]);

  const scale = effectiveTimeline.scale ?? 'day';
  const timelineSlots = useMemo(() => {
    const t = effectiveTimeline;
    if (
      t.viewStart === null ||
      t.viewStart === undefined ||
      t.viewEnd === null ||
      t.viewEnd === undefined
    ) {
      return [];
    }
    const sc = (t.scale ?? 'day') as GhanttTimelineScale;
    return ghanttBuildTimelineSlots(t.viewStart, t.viewEnd, sc, {
      includeLeadingFullMonth: sc === 'day' ? t.includeLeadingFullMonth !== false : undefined,
    });
  }, [effectiveTimeline]);

  const timelineSlotsLenRef = useRef(0);
  const cellSnapForVirtualRef = useRef(2);
  const scrollVirtualRafRef = useRef<number | null>(null);
  const logicalContentWidthRef = useRef(0);

  timelineSlotsLenRef.current = timelineSlots.length;
  const currentCell = ghanttSnapSlotWidthPx(
    effectiveTimeline.columnWidthPx ?? ghanttDefaultColumnWidthForScale(scale)
  );
  cellSnapForVirtualRef.current = currentCell;

  // Si no hay un zoom pendiente, sincronizamos el ancho lógico con lo que hay en pantalla
  useLayoutEffect(() => {
    if (zoomScrollAdjustRef.current === null) {
      logicalContentWidthRef.current = timelineSlots.length * currentCell;
    }
  }, [timelineSlots.length, currentCell]);

  const needsScrollVirtual = useMemo(
    () =>
      (scale === 'hour' || scale === 'day' || scale === 'week') &&
      timelineSlots.length >= GHANTT_SCROLL_VIRTUAL_MIN_SLOTS,
    [scale, timelineSlots.length]
  );

  const upperSegments = useMemo(
    () => (timelineSlots.length > 0 ? ghanttBuildUpperFloorSegments(timelineSlots, scale) : []),
    [timelineSlots, scale]
  );

  const dayAxisValid = useMemo(() => {
    if (scale !== 'day') return null;
    const dayAxis = ghanttResolveExpandedDayRange(
      effectiveTimeline.viewStart,
      effectiveTimeline.viewEnd,
      {
        includeLeadingFullMonth: effectiveTimeline.includeLeadingFullMonth !== false,
      }
    );
    return dayAxis &&
      Number.isFinite(dayAxis.startMs) &&
      Number.isFinite(dayAxis.endExclusiveMs) &&
      dayAxis.endExclusiveMs > dayAxis.startMs
      ? dayAxis
      : null;
  }, [
    scale,
    effectiveTimeline.viewStart,
    effectiveTimeline.viewEnd,
    effectiveTimeline.includeLeadingFullMonth,
  ]);

  const headerViewportRef = useRef<HTMLDivElement | null>(null);
  const scrollLeftRef = useRef(0);
  const bodyTracksRef = useRef(new Set<HTMLElement>());
  /** Tras zoom, reajusta scroll para mantener el mismo instante bajo el cursor (rejilla de slots, no solo viewStart–viewEnd lineal). */
  const zoomScrollAdjustRef = useRef<{ anchorMs: number; xInViewport: number } | null>(null);
  /** Agrupa deltas de rueda en un único `requestAnimationFrame` (menos “va y vuelve” en trackpads). */
  const wheelBatchDyRef = useRef(0);
  const wheelRafRef = useRef<number | null>(null);
  const lastWheelUiRef = useRef({ anchorFraction: 0.5, xInViewport: 0 });
  const lastModsRef = useRef({ ctrlKey: false, metaKey: false, altKey: false });
  const lastBodySyncScrollRef = useRef<number | null>(null);

  const flushWheelZoom = useCallback(() => {
    wheelRafRef.current = null;
    const totalDy = wheelBatchDyRef.current;
    wheelBatchDyRef.current = 0;
    if (totalDy === 0) return;

    const mods = lastModsRef.current;
    const ui = lastWheelUiRef.current;

    const prev = managedTimelineRef.current;
    const vs0 = ghanttParseTime(prev.viewStart);
    const ve0 = ghanttParseTime(prev.viewEnd);
    if (!Number.isFinite(vs0) || !Number.isFinite(ve0) || ve0 <= vs0) return;

    const next =
      ghanttModifierZoomWheelDelta(prev, {
        deltaY: totalDy,
        ctrlKey: mods.ctrlKey,
        metaKey: mods.metaKey,
        altKey: mods.altKey,
        anchorFraction: ui.anchorFraction,
      }) ?? null;
    if (next === null) return;

    const sc = (prev.scale ?? 'day') as GhanttTimelineScale;
    const prevSlots = ghanttBuildTimelineSlots(prev.viewStart, prev.viewEnd, sc, {
      includeLeadingFullMonth: sc === 'day' ? prev.includeLeadingFullMonth !== false : undefined,
    });
    const slotW = prev.columnWidthPx ?? ghanttDefaultColumnWidthForScale(sc);
    const headerEl = headerViewportRef.current;
    const scrollFromDom =
      headerEl !== null && Number.isFinite(headerEl.scrollLeft)
        ? headerEl.scrollLeft
        : scrollLeftRef.current;
    if (headerEl !== null) {
      scrollLeftRef.current = scrollFromDom;
    }
    const cellSnap = ghanttSnapSlotWidthPx(slotW);
    const maxContentPx = Math.max(0, prevSlots.length * cellSnap);
    const anchorContentPx = Math.max(0, Math.min(maxContentPx, scrollFromDom + ui.xInViewport));
    const fromGrid = ghanttContentPixelToAnchorMs(prevSlots, sc, slotW, anchorContentPx);
    const anchorMs =
      fromGrid !== null && fromGrid !== undefined && Number.isFinite(fromGrid)
        ? fromGrid
        : vs0 + ui.anchorFraction * (ve0 - vs0);
    zoomScrollAdjustRef.current = { anchorMs, xInViewport: ui.xInViewport };

    setManagedTimeline(next);

    // Sincronizamos el ancho lógico inmediatamente para que el siguiente evento de rueda
    // en este mismo ciclo use la base correcta incluso antes del re-render de React.
    const scNext = (next.scale ?? 'day') as GhanttTimelineScale;
    const nextSlots = ghanttBuildTimelineSlots(next.viewStart, next.viewEnd, scNext, {
      includeLeadingFullMonth:
        scNext === 'day' ? next.includeLeadingFullMonth !== false : undefined,
    });
    const nextCell = ghanttSnapSlotWidthPx(next.columnWidthPx ?? 46);
    logicalContentWidthRef.current = nextSlots.length * nextCell;
  }, [setManagedTimeline]);

  useEffect(() => {
    return () => {
      if (wheelRafRef.current !== null) {
        cancelAnimationFrame(wheelRafRef.current);
        wheelRafRef.current = null;
      }
      if (scrollVirtualRafRef.current !== null) {
        cancelAnimationFrame(scrollVirtualRafRef.current);
        scrollVirtualRafRef.current = null;
      }
    };
  }, []);

  const syncBodyTracks = useCallback((v: number) => {
    if (lastBodySyncScrollRef.current === v) return;
    lastBodySyncScrollRef.current = v;
    const tx = `translate3d(-${v}px,0,0)`;
    for (const node of bodyTracksRef.current) {
      node.style.transform = tx;
    }
  }, []);

  const flushScrollVirtualRange = useCallback(() => {
    if (!needsScrollVirtual) {
      scrollVirtualStore.setIdle();
      return;
    }
    const el = headerViewportRef.current;
    if (!el) {
      scrollVirtualStore.setManagedRange(null);
      return;
    }
    const n = timelineSlotsLenRef.current;
    const cell = cellSnapForVirtualRef.current;
    const r = ghanttScrollVirtualIndexRange(el.scrollLeft, el.clientWidth, cell, n);
    scrollVirtualStore.setManagedRange(r);
  }, [needsScrollVirtual, scrollVirtualStore]);

  const scheduleScrollVirtualFlush = useCallback(() => {
    if (!needsScrollVirtual) return;
    if (scrollVirtualRafRef.current !== null) return;
    scrollVirtualRafRef.current = requestAnimationFrame(() => {
      scrollVirtualRafRef.current = null;
      flushScrollVirtualRange();
    });
  }, [needsScrollVirtual, flushScrollVirtualRange]);

  useLayoutEffect(() => {
    flushScrollVirtualRange();
    if (!needsScrollVirtual) return;
    let cancelled = false;
    let raf = 0;
    const retry = () => {
      if (cancelled) return;
      if (headerViewportRef.current) {
        flushScrollVirtualRange();
        return;
      }
      raf = requestAnimationFrame(retry);
    };
    if (!headerViewportRef.current) {
      raf = requestAnimationFrame(retry);
    }
    return () => {
      cancelled = true;
      if (raf !== 0) cancelAnimationFrame(raf);
    };
  }, [timelineScrollKey, needsScrollVirtual, flushScrollVirtualRange]);

  useEffect(() => {
    if (!needsScrollVirtual) return;
    const onResize = () => {
      flushScrollVirtualRange();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [needsScrollVirtual, flushScrollVirtualRange]);

  const registerBodyTimeline = useCallback((el: HTMLElement | null) => {
    if (!el) return () => {};
    bodyTracksRef.current.add(el);
    const v = scrollLeftRef.current;
    lastBodySyncScrollRef.current = v;
    el.style.transform = `translate3d(-${v}px,0,0)`;
    el.style.willChange = 'transform';
    return () => {
      bodyTracksRef.current.delete(el);
    };
  }, []);

  const setScrollLeft = useCallback(
    (x: number, manualMax?: number) => {
      const el = headerViewportRef.current;
      const effectiveMax = manualMax ?? (el ? Math.max(0, el.scrollWidth - el.clientWidth) : 0);
      const raw = Number.isFinite(x) ? x : 0;
      const v = Math.max(0, Math.min(effectiveMax, raw));
      const domOk = !el || Math.abs(el.scrollLeft - v) < 0.5;
      const refOk = Math.abs(scrollLeftRef.current - v) < 0.5;
      if (refOk && domOk && lastBodySyncScrollRef.current === v) {
        return;
      }
      scrollLeftRef.current = v;
      syncBodyTracks(v);
      if (el && el.scrollLeft !== v) {
        el.scrollLeft = v;
      }
      scheduleScrollVirtualFlush();
    },
    [syncBodyTracks, scheduleScrollVirtualFlush]
  );

  const onHeaderViewportScroll = useCallback<UIEventHandler<HTMLDivElement>>(
    (e) => {
      const v = e.currentTarget.scrollLeft;
      scrollLeftRef.current = v;
      syncBodyTracks(v);
      scheduleScrollVirtualFlush();
    },
    [syncBodyTracks, scheduleScrollVirtualFlush]
  );

  const onBodyWheel = useCallback<WheelEventHandler<HTMLDivElement>>(
    (e) => {
      if (modifierWheelZoom && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        e.stopPropagation();

        const wheelTl = managedTimelineRef.current;

        const headerEl = headerViewportRef.current;
        const host = e.currentTarget as HTMLElement;
        const xInViewport = ghanttWheelTimelineViewportClientX(headerEl ?? host, e.clientX);

        const currentLogicalWidth =
          logicalContentWidthRef.current > 0
            ? logicalContentWidthRef.current
            : headerEl?.scrollWidth || 1;

        const anchorFraction = Math.max(
          0,
          Math.min(1, (scrollLeftRef.current + xInViewport) / currentLogicalWidth)
        );

        lastWheelUiRef.current = { anchorFraction, xInViewport };
        lastModsRef.current = {
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          altKey: e.altKey,
        };

        const raw =
          e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * 800 : e.deltaY;
        const capped = Math.sign(raw) * Math.min(Math.abs(raw), 150);
        wheelBatchDyRef.current += capped;

        if (wheelRafRef.current === null) {
          wheelRafRef.current = requestAnimationFrame(flushWheelZoom);
        }
        return;
      }
      const d = e.deltaX !== 0 ? e.deltaX : e.shiftKey && e.deltaY !== 0 ? e.deltaY : 0;
      if (d === 0) return;
      e.preventDefault();
      setScrollLeft(scrollLeftRef.current + d);
    },
    [modifierWheelZoom, setScrollLeft, flushWheelZoom]
  );

  useLayoutEffect(() => {
    const el = headerViewportRef.current;
    const pending = zoomScrollAdjustRef.current;
    if (pending && modifierWheelZoom && el) {
      zoomScrollAdjustRef.current = null;
      const sc = (effectiveTimeline.scale ?? 'day') as GhanttTimelineScale;
      const cell = ghanttSnapSlotWidthPx(
        effectiveTimeline.columnWidthPx ?? ghanttDefaultColumnWidthForScale(sc)
      );
      const expectedW = timelineSlots.length * cell;
      if (timelineSlots.length > 0 && el.scrollWidth > 0 && expectedW > 0) {
        const contentPx = ghanttAnchorMsToContentPixel(
          timelineSlots,
          sc,
          effectiveTimeline.columnWidthPx ?? ghanttDefaultColumnWidthForScale(sc),
          pending.anchorMs
        );
        if (contentPx !== null && contentPx !== undefined && Number.isFinite(contentPx)) {
          const target = contentPx - pending.xInViewport;
          setScrollLeft(target, Math.max(0, expectedW - el.clientWidth));
          return;
        }
      }
      const vs = ghanttParseTime(effectiveTimeline.viewStart);
      const ve = ghanttParseTime(effectiveTimeline.viewEnd);
      const span = ve - vs;
      if (span > 0 && el.scrollWidth > 0) {
        const frac = (pending.anchorMs - vs) / span;
        const target = frac * el.scrollWidth - pending.xInViewport;
        setScrollLeft(target, Math.max(0, el.scrollWidth - el.clientWidth));
        return;
      }
    }
    setScrollLeft(scrollLeftRef.current);
  }, [
    timelineScrollKey,
    modifierWheelZoom,
    effectiveTimeline.viewStart,
    effectiveTimeline.viewEnd,
    effectiveTimeline.scale,
    effectiveTimeline.columnWidthPx,
    timelineSlots,
    setScrollLeft,
  ]);

  const horizontalScroll = useMemo(
    () => ({
      headerViewportRef,
      setScrollLeft,
      onHeaderViewportScroll,
      onBodyWheel,
      registerBodyTimeline,
    }),
    [setScrollLeft, onHeaderViewportScroll, onBodyWheel, registerBodyTimeline]
  );

  const contextValue: GhanttConfigContextValue<T> = useMemo(
    () => ({
      timeline: timelineForContext,
      appearance,
      events,
      timelineSlots,
      upperSegments,
      dayAxis: dayAxisValid,
      horizontalScroll,
    }),
    [
      timelineForContext,
      appearance,
      events,
      timelineSlots,
      upperSegments,
      dayAxisValid,
      horizontalScroll,
    ]
  );

  return (
    <GhanttConfigProvider value={contextValue}>
      <GhanttScrollVirtualRoot store={scrollVirtualStore}>
        <Table {...(tableProps as TableProps<T>)} columns={mergedColumns} />
      </GhanttScrollVirtualRoot>
    </GhanttConfigProvider>
  );
}

Ghantt.displayName = 'Ghantt';
